import { css } from '@emotion/react'
import { Cell, FormControl, HFlow, Icon, Tooltip } from 'bold-ui'
import { ColorScale } from 'bold-ui/lib/styles/colors'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import theme from 'config/theme'
import { UnidadeSaude } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { Fragment } from 'react'
import { useField } from 'react-final-form'
import { isUndefinedOrNull } from 'util/checks'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { MetaRoot } from 'util/metaPath'

import { EstabelecimentosCuidadoCompartilhadoSelectField } from '../components/EstabelecimentosCuidadoCompartilhadoSelectField'
import { LotacoesCuidadoCompartilhadoSelectField } from '../components/LotacoesCuidadoCompartilhadoSelectField'
import { CuidadoCompartilhadoPlanoModel } from '../model-cuidadocompartilhado'

interface CuidadoCompartilhadoUnidadeLotacaoFieldsProps {
  path: MetaRoot<CuidadoCompartilhadoPlanoModel>
  cboCodigo2002: string
  isGuiaEncaminhamento: boolean
  isCuidadoCompartilhado: boolean
  cboId: ID
  isEdit: boolean
  disabled: boolean
}

export default function CuidadoCompartilhadoUnidadeLotacaoFields(
  props: Readonly<CuidadoCompartilhadoUnidadeLotacaoFieldsProps>
) {
  const { path, cboCodigo2002, isGuiaEncaminhamento, isCuidadoCompartilhado, cboId, isEdit, disabled } = props

  const { iniciadoEm } = useAtendimentoContext()

  const { acesso, profissional } = useAcessoLotacaoOrEstagio()

  const {
    input: { value: unidadeSaudeValue },
  } = useField<UnidadeSaude>(path.unidadeSaude.absolutePath(), { subscription: { value: true } })

  const unidadeSelecionadaId = unidadeSaudeValue?.id
  const isEMulti = isCuidadoCompartilhado && acesso.unidadeSaude.id === unidadeSelecionadaId

  const unidadeSaudeId = isEMulti ? acesso.unidadeSaude.id : unidadeSelecionadaId
  const equipeSolicitanteId = isEMulti ? acesso.equipe?.id : null

  const unidadeRequired = isCuidadoCompartilhado
  const unidadeDisabled = (isEdit && isGuiaEncaminhamento) || disabled
  const profissionalRequired = !disabled && isEMulti
  const profissionalDisabled = (isEdit && isGuiaEncaminhamento) || disabled || isUndefinedOrNull(unidadeSaudeId)

  return (
    <Fragment>
      <Cell size={6}>
        <EstabelecimentosCuidadoCompartilhadoSelectField
          name={path.unidadeSaude}
          label='Unidade de saúde/Serviço de referência'
          cboCodigo2002={cboCodigo2002}
          disabled={unidadeDisabled}
          required={unidadeRequired}
        />
      </Cell>

      <Cell size={6}>
        <FormControl label='Profissional' required={profissionalRequired}>
          <HFlow hSpacing={0.5} alignItems='baseline' style={styles.hFlow}>
            <LotacoesCuidadoCompartilhadoSelectField
              name={path.lotacao}
              unidadeSaudeId={unidadeSaudeId}
              cboId={cboId}
              excludeProfissionaisIds={[profissional.id]}
              equipeSolicitanteId={equipeSolicitanteId}
              required={profissionalRequired}
              disabled={profissionalDisabled}
              date={dateAsYyyyMmDd(iniciadoEm)}
            />
            {isEMulti && (
              <Tooltip
                text='O campo só traz profissionais de equipes NASF e eMulti vinculados a essa unidade.'
                placement='top'
              >
                <Icon icon='infoCircleFilled' fill='normal' size={1} style={styles.iconTooltip} />
              </Tooltip>
            )}
          </HFlow>
        </FormControl>
      </Cell>
    </Fragment>
  )
}

const styles = {
  hFlow: css`
    grid-auto-columns: 1fr auto;
  `,
  iconTooltip: css`
    &:hover {
      fill: ${theme.pallete.primary.main};
    }
  `,
  icon: (color: ColorScale) => css`
    height: 1.5rem;
    width: 1.5rem;
    filter: drop-shadow(0 0.08rem 0.1rem ${color.c60});
  `,
  registrarHeading: (color: ColorScale) => css`
    color: ${color.c40};
  `,
}
